import { O, S } from 'ts-toolbelt';

type PathValue<T, P extends string> = O.Path<T & {}, S.Split<P, '.'>>;

/**
 * Type-safe get for nested properties by dot-separated path.
 *
 * - Type is inferred automatically from obj and path via ts-toolbelt.
 * - DefaultValue type is included in the return type via union.
 */

/** Typed obj (non-nullable), no default → inferred type */
export function get<T extends object, P extends string>(
	obj: T,
	path: P
): PathValue<T, P>;

/** Typed obj (nullable), no default → inferred type | undefined */
export function get<T extends object, P extends string>(
	obj: T | null | undefined,
	path: P
): PathValue<T, P> | undefined;

/** Typed obj, with default → inferred type (excluding undefined) | typeof default */
export function get<T extends object, P extends string, D>(
	obj: T | null | undefined,
	path: P,
	defaultValue: D
): Exclude<PathValue<T, P>, undefined> | D;

/** Implementation */
export function get(
	obj: unknown,
	path: string,
	defaultValue?: unknown
): unknown {
	const keys = path.split('.');
	let result: unknown = obj;

	for (const key of keys) {
		if (result == null) {
			return defaultValue !== undefined ? defaultValue : undefined;
		}
		result = (result as Record<string, unknown>)[key];
	}

	if (result === undefined) {
		return defaultValue !== undefined ? defaultValue : undefined;
	}

	return result;
}
